import { create } from 'zustand';

import { CONFIG } from 'src/environments/environment';

import type { UpdatePolicyState } from 'src/types/update-policy';

import {
  getApiBaseUrlOverride,
  setApiBaseUrlOverrideStorage,
  API_BASE_URL_OVERRIDE_ALLOWED,
} from './api-base-url-override';
import {
  DeviceType,
  SystemStoreType,
  BleReaderStatus,
  BlePaymentStatus,
  UpdateHistoryItem,
  TerminalConnectionStatus,
} from './types';

// Read the persisted debug override (if any) synchronously at store creation so it is
// applied before the first API request. Falls back to the build-time default. A
// production build has no override (api-base-url-override.ts).
const persistedApiBaseUrlOverride = getApiBaseUrlOverride();

export const useSystemStore = create<SystemStoreType>((set, get) => ({
  shouldRefetchPreload: false,
  baseUrl: persistedApiBaseUrlOverride ?? CONFIG.VITE_HOST_API,
  apiBaseUrlOverride: persistedApiBaseUrlOverride,
  baseAthUrl: CONFIG.VITE_ATH_API,
  basePublicUrl: CONFIG.VITE_PUBLIC_API,
  currentDeviceType: DeviceType.DESKTOP,
  appsToDisplayOnMobile: [],
  appVersion: {
    current: CONFIG.VITE_APP_VERSION,
    minimum: '0.0.0',
    native: undefined,
  },
  featureFlags: {
    elevateAiEnabled: false,
  },
  // Terminal connection initial state
  terminalConnection: {
    status: TerminalConnectionStatus.DISCONNECTED,
    lastConnected: null,
    lastError: null,
  },
  // BLE Reader initial state
  bleReaderStatus: BleReaderStatus.NOT_INITIALIZED,
  bleReader: null,
  bleDiscoveredReaders: [],
  bleReaderBattery: {
    level: null,
    isCharging: false,
    status: 'unknown',
  },
  bleReaderError: null,
  bleReaderUpdateProgress: null,
  blePaymentStatus: BlePaymentStatus.IDLE,
  isPrinting: false,
  // Update-related initial state
  updateStatus: 'idle',
  availableUpdate: null,
  downloadProgress: 0,
  updateHistory: [],
  lastUpdateCheck: null,
  updateError: null,
  // Update policy state
  updatePolicy: {
    globalPolicy: null,
    businessPolicy: null,
    mergedPolicy: null,
    loading: false,
    error: null,
    lastFetch: null,
  },
  // Developer toggle for V4 payment redirect (in-memory only)
  useV4Payment: true,
  // Payment page debug mode toggle (in-memory only)
  showPaymentDebugMode: false,
  // BlockChyp terminal debug log overlay toggle (in-memory only)
  showBlockchypDebugMode: false,
  // Stripe M2 terminal debug log overlay toggle (in-memory only)
  showStripeM2DebugMode: false,
  // Outgoing API history capture toggle (in-memory only)
  apiCallCaptureEnabled: false,
  // Payment flow state for Footer visibility (in-memory only)
  // Quick Pay panel open state (in-memory only)
  v4QuickPayPanelOpen: false,

  // Existing setters
  setFeatureFlags: (value) =>
    set((state) => ({ featureFlags: { ...state.featureFlags, ...value } })),
  setResolvedAppVersion: (value) =>
    set((state) => ({ appVersion: { ...state.appVersion, ...value } })),
  setMinimumAppVersion: (minimum) =>
    set((state) => ({ appVersion: { ...state.appVersion, minimum: minimum || '0.0.0' } })),
  setBaseUrl: (value) => set((state) => ({ baseUrl: value.length ? value : state.baseUrl })),
  setBaseAthUrl: (value) =>
    set((state) => ({ baseAthUrl: value.length ? value : state.baseAthUrl })),
  setBasePublicUrl: (value) =>
    set((state) => ({ basePublicUrl: value.length ? value : state.basePublicUrl })),
  // Debug-only API base URL override: persists to localStorage and applies immediately
  // (axios reads baseUrl per request). An active override wins over Firebase Remote Config.
  setApiBaseUrlOverride: (url) => {
    const trimmed = url.trim();
    if (!trimmed.length || !API_BASE_URL_OVERRIDE_ALLOWED) return;
    setApiBaseUrlOverrideStorage(trimmed);
    set({ baseUrl: trimmed, apiBaseUrlOverride: trimmed });
  },
  clearApiBaseUrlOverride: () => {
    setApiBaseUrlOverrideStorage(null);
    set({ baseUrl: CONFIG.VITE_HOST_API, apiBaseUrlOverride: null });
  },
  setShouldRefetchPreload: (value) => set({ shouldRefetchPreload: value }),
  setCurrentDeviceType: (value) => set({ currentDeviceType: value }),
  setAppsToDisplayOnMobile: (value) => set({ appsToDisplayOnMobile: value }),
  // BLE Reader setters
  setBleReaderStatus: (value) => set({ bleReaderStatus: value }),
  setBleReader: (value) => set({ bleReader: value }),
  setBleDiscoveredReaders: (value) => set({ bleDiscoveredReaders: value }),
  setBleReaderBattery: (value) => set({ bleReaderBattery: value }),
  setBleReaderError: (value) => set({ bleReaderError: value }),
  setBleReaderUpdateProgress: (value) => set({ bleReaderUpdateProgress: value }),
  setBlePaymentStatus: (value) => set({ blePaymentStatus: value }),
  setIsPrinting: (value) => set({ isPrinting: value }),
  // Update-related setters
  setUpdateStatus: (value) => set({ updateStatus: value }),
  setAvailableUpdate: (value) => set({ availableUpdate: value }),
  setDownloadProgress: (value) => set({ downloadProgress: value }),
  setUpdateHistory: (value) => set({ updateHistory: value }),
  setLastUpdateCheck: (value) => set({ lastUpdateCheck: value }),
  setUpdateError: (value) => set({ updateError: value }),
  addToUpdateHistory: (item: UpdateHistoryItem) =>
    set((state) => ({
      updateHistory: [item, ...state.updateHistory].slice(0, 10), // Keep only last 10 entries
    })),
  // Update policy setters
  setUpdatePolicy: (value: Partial<UpdatePolicyState>) =>
    set((state) => ({
      updatePolicy: { ...state.updatePolicy, ...value },
    })),
  setGlobalPolicy: (value) =>
    set((state) => ({
      updatePolicy: { ...state.updatePolicy, globalPolicy: value },
    })),
  setBusinessPolicy: (value) =>
    set((state) => ({
      updatePolicy: { ...state.updatePolicy, businessPolicy: value },
    })),
  setMergedPolicy: (value) =>
    set((state) => ({
      updatePolicy: { ...state.updatePolicy, mergedPolicy: value },
    })),
  setUpdatePolicyLoading: (value) =>
    set((state) => ({
      updatePolicy: { ...state.updatePolicy, loading: value },
    })),
  setUpdatePolicyError: (value) =>
    set((state) => ({
      updatePolicy: { ...state.updatePolicy, error: value },
    })),
  setUseV4Payment: (value) => set({ useV4Payment: value }),
  setShowPaymentDebugMode: (value) => set({ showPaymentDebugMode: value }),
  setShowBlockchypDebugMode: (value) => set({ showBlockchypDebugMode: value }),
  setShowStripeM2DebugMode: (value) => set({ showStripeM2DebugMode: value }),
  setApiCallCaptureEnabled: (value) => set({ apiCallCaptureEnabled: value }),
  setV4QuickPayPanelOpen: (value) => set({ v4QuickPayPanelOpen: value }),
  // Terminal connection setters
  setTerminalConnectionStatus: (status) =>
    set((state) => ({
      terminalConnection: {
        ...state.terminalConnection,
        status,
      },
    })),
  setTerminalConnectionLastConnected: (date) =>
    set((state) => ({
      terminalConnection: {
        ...state.terminalConnection,
        lastConnected: date,
      },
    })),
  setTerminalConnectionError: (error) =>
    set((state) => ({
      terminalConnection: {
        ...state.terminalConnection,
        lastError: error,
      },
    })),
  resetTerminalConnection: () =>
    set({
      terminalConnection: {
        status: TerminalConnectionStatus.DISCONNECTED,
        lastConnected: null,
        lastError: null,
      },
    }),
}));

/**
 *

  const { example, exampleVar: false } = useExampleDataStore((state) => ({
    currentInventory: state.currentInventory,
    newInventory: state.newInventory,
  }));

  const setExample = useExampleDataStore.getState().setExample();

 */
